import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import {
  browserLocalPersistence,
  getRedirectResult,
  getAuth,
  GoogleAuthProvider,
  setPersistence,
  signInAnonymously,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import {
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
  onMessage,
} from "firebase/messaging";
import type { MessagePayload, Messaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyC5DE5gqViPYwoZPVT-lpqqIUDnDu0v1F0",
  authDomain: "spirit-garden-51493.firebaseapp.com",
  projectId: "spirit-garden-51493",
  storageBucket: "spirit-garden-51493.firebasestorage.app",
  messagingSenderId: "1048103110419",
  appId: "1:1048103110419:web:843d91b341721cf54c3ce6",
  measurementId: "G-G31KSBFTGW",
};

const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const auth = getAuth(app);
export const GOOGLE_REDIRECT_PENDING_KEY = "sg_google_redirect_pending_v1";
export const GOOGLE_AUTH_REDIRECT_URIS = [
  "https://spiritgarden.app/__/auth/handler",
  "https://spirit-garden-51493.firebaseapp.com/__/auth/handler",
  "https://spirit-garden-51493.web.app/__/auth/handler",
];

const authPersistenceReady = setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.warn("Could not set Firebase auth persistence", error);
});

export const FIREBASE_VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

let messagingPromise: Promise<Messaging | null> | null = null;

export function getFirebaseConfig() {
  return firebaseConfig;
}

export function getFirebaseMessaging() {
  if (!messagingPromise) {
    messagingPromise = isMessagingSupported()
      .then((supported) => (supported ? getMessaging(app) : null))
      .catch(() => null);
  }
  return messagingPromise;
}

export async function requestPushToken() {
  if (FIREBASE_VAPID_KEY.includes("PASTE_YOUR")) {
    throw new Error("missing-vapid-key");
  }
  if (!window.isSecureContext) {
    throw new Error("notifications-need-https");
  }
  if (!("Notification" in window)) {
    throw new Error("notifications-not-supported");
  }
  if (!("serviceWorker" in navigator)) {
    throw new Error("service-worker-not-supported");
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("notification-permission-denied");
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    throw new Error("messaging-not-supported");
  }

  try {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    await registration.update().catch(() => {});

    return await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (error: any) {
    throw new Error(
      `push-token-failed:${error?.code || error?.message || "unknown"}`
    );
  }
}

export async function listenForForegroundMessages(
  callback: (payload: MessagePayload) => void
) {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

export async function ensureAnonymousUser() {
  await authPersistenceReady;
  if (auth.currentUser) return auth.currentUser;
  if (isGoogleRedirectPending()) return null;

  const userCredential = await signInAnonymously(auth);
  return userCredential.user;
}

export async function signInWithGoogle() {
  // Prefer popup sign-in because iPhone Home Screen/PWA mode can lose redirect
  // state. If popup is blocked in a normal browser, fall back to redirect there.
  clearGoogleRedirectPending();
  clearGoogleAuthStorage();
  await authPersistenceReady.catch(() => {});
  const provider = createGoogleProvider();
  const currentUser = auth.currentUser;

  if (currentUser?.isAnonymous) {
    await signOut(auth).catch(() => {});
  }

  if (isSignedInWithGoogle(auth.currentUser)) {
    return auth.currentUser;
  }

  try {
    const credential = await withAuthTimeout(signInWithPopup(auth, provider));
    clearGoogleRedirectPending();
    return credential.user;
  } catch (error: any) {
    clearGoogleAuthStorage();
    const message = String(error?.message || "");
    const popupCouldNotOpen =
      error?.code === "auth/popup-blocked" ||
      error?.code === "auth/cancelled-popup-request" ||
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/popup-timeout";

    if (isStandaloneHomeScreenApp()) {
      const redirectMismatch = message.includes("redirect_uri_mismatch");
      const pwaError = new Error(
        redirectMismatch
          ? "Google sign-in redirect URI is not authorized for the installed app."
          : "Google sign-in could not open inside the installed app. Please try again."
      ) as Error & { code?: string; originalCode?: string };
      pwaError.code = redirectMismatch
        ? "auth/redirect-uri-mismatch"
        : popupCouldNotOpen
        ? "auth/pwa-popup-blocked"
        : error?.code || "auth/pwa-sign-in-failed";
      pwaError.originalCode = error?.code;
      throw pwaError;
    }

    if (popupCouldNotOpen) {
      markGoogleRedirectPending();
      await signInWithRedirect(auth, provider);
      const redirectError = new Error("Redirect started") as Error & { code?: string };
      redirectError.code = "auth/redirect-started";
      throw redirectError;
    }

    throw error;
  }
}

export async function completeGoogleRedirectSignIn() {
  await authPersistenceReady.catch(() => {});
  const result = await getRedirectResult(auth);
  if (result?.user) {
    clearGoogleRedirectPending();
    return result.user;
  }
  return auth.currentUser;
}

export function signOutUser() {
  clearGoogleRedirectPending();
  clearGoogleAuthStorage();
  return signOut(auth);
}

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope("email");
  provider.addScope("profile");
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

function isSignedInWithGoogle(user: typeof auth.currentUser) {
  return Boolean(
    user &&
      !user.isAnonymous &&
      user.providerData.some((providerInfo) => providerInfo.providerId === "google.com")
  );
}

function isStandaloneHomeScreenApp() {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    nav.standalone === true
  );
}

async function withAuthTimeout<T>(promise: Promise<T>) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          const error = new Error("Google sign-in took too long to open.") as Error & { code?: string };
          error.code = "auth/popup-timeout";
          reject(error);
        }, 12000);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export function markGoogleRedirectPending() {
  if (typeof window === "undefined") return;
  const value = String(Date.now());
  try {
    window.sessionStorage.setItem(GOOGLE_REDIRECT_PENDING_KEY, value);
    window.localStorage.setItem(GOOGLE_REDIRECT_PENDING_KEY, value);
  } catch {}
}

export function clearGoogleRedirectPending() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
    window.localStorage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
  } catch {}
}

export function clearGoogleAuthStorage() {
  if (typeof window === "undefined") return;
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.includes("firebase") || key.includes("google"))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {}
}

export function clearStaleGoogleRedirectPending() {
  if (typeof window === "undefined") return;
  try {
    const raw =
      window.sessionStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY) ||
      window.localStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY);
    const startedAt = Number(raw || 0);
    if (!startedAt || Date.now() - startedAt > 10 * 60 * 1000) {
      clearGoogleRedirectPending();
    }
  } catch {
    clearGoogleRedirectPending();
  }
}

export function isGoogleRedirectPending() {
  if (typeof window === "undefined") return false;
  clearStaleGoogleRedirectPending();
  try {
    return Boolean(
      window.sessionStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY) ||
      window.localStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY)
    );
  } catch {
    return false;
  }
}
